// ─── Misc Query Hooks ───────────────────────────────────────────────
// Hooks for dog SVG, trail history, and other misc data.

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { API_URL } from '@/lib/api';
import { fetchMyTrailsHistory, type TrailPage } from '@/lib/api/trails';
import type { Trail } from '@/types/trail';

/** Fetch the dog SVG for profile display. Cached for 30 minutes. */
export function useDogSvg() {
  return useQuery({
    queryKey: queryKeys.misc.dogSvg,
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/uploads/proxy/stats/dog.svg`);
      if (!resp.ok) return null;
      const text = await resp.text();
      return text || null;
    },
    staleTime: 30 * 60 * 1000, // 30 min
    gcTime: 60 * 60 * 1000,    // 60 min
  });
}

/** Fetch trail history (walked trails with distance from user), paginated. */
export function useTrailHistory(token: string | undefined, enabled: boolean, dogId?: string) {
  return useInfiniteQuery<TrailPage, Error>({
    queryKey: ['trails', 'history', dogId ?? null] as const,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchMyTrailsHistory(token!, dogId, pageParam as number),
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    enabled: !!token && enabled,
    staleTime: 2 * 60 * 1000, // 2 min
  });
}

/** Flatten a trail-history infinite query into a plain list. */
export function flattenHistoryPages(data: InfiniteData<TrailPage> | undefined): Trail[] {
  return data?.pages.flatMap((p) => p.trails) ?? [];
}